<template>
  <div
    v-if="!editMode"
    class="tw-flex tw-items-center"
    :class="{
      'tw-group tw-cursor-text tw-transition-colors hover:tw-bg-gp-midnight-2': editable,
    }"
    v-bind="attrs"
    :title="title"
  >
    <component
      :is="type"
      class="tw-block tw-h-[40px] tw-flex-1 tw-truncate tw-rounded tw-pt-[2px] tw-text-4xl tw-font-bold tw-leading-none"
      :style="{
        'text-transform': disableUpper ? 'none' : undefined,
      }"
      @click="toggle"
    >
      <slot />
    </component>

    <GpBtn
      v-if="editable"
      class="tw-ml-2 tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-100"
      :icon="['fas', 'fa-pen']"
      btn-type="primary"
      size="sm"
      @click="toggle"
    />
  </div>

  <div
    v-else
    class="tw-flex tw-items-center tw-gap-2 tw-rounded tw-bg-gp-midnight-2"
    v-bind="attrs"
  >
    <input
      class="tw-h-[40px] tw-w-full tw-flex-1 tw-bg-transparent tw-text-4xl tw-font-bold tw-leading-none tw-outline-none"
      :class="{
        'tw-pointer-events-none tw-opacity-80': isSubmittingTitle,
      }"
      ref="textInput"
      type="text"
      v-model="inputValue"
      :disabled="isSubmittingTitle"
      :placeholder="title"
      @keydown.enter.prevent="changeValue"
      @keydown.esc.prevent="dismiss"
    />

    <GpBtn
      :icon="['fas', 'fa-check']"
      btn-type="success"
      size="sm"
      data-headline-edit="submit"
      :disabled="isSubmittingTitle"
      @click="() => changeValue()"
    />
    <GpBtn
      :disabled="isSubmittingTitle"
      :icon="['fas', 'fa-times']"
      btn-type="danger"
      size="sm"
      data-headline-edit="cancel"
      @click="dismiss"
    />
  </div>
</template>

<script lang="ts">
import { type PropType, defineComponent, nextTick, ref, useAttrs, watch } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { GpBtn, useFaIcons } from '@gportal/uikit';
import { faPen } from '@fortawesome/pro-solid-svg-icons';

export enum HeadlineType {
  H1 = 'h1',
  H2 = 'h2',
  H3 = 'h3',
  H4 = 'h4',
  H5 = 'h5',
}

export default defineComponent({
  props: {
    type: {
      type: String as PropType<HeadlineType>,
      required: true,
    },
    title: {
      type: String,
      default: '',
    },
    editable: {
      type: Boolean,
      default: false,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    isSubmittingTitle: {
      type: Boolean,
      default: false,
    },
    disableUpper: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['value-changed', 'toggle-edit'],
  setup(props, { emit }) {
    useFaIcons({ faPen });

    const textInput = ref<HTMLInputElement>();
    const editMode = ref<boolean>(props.isEditing);
    watch(
      () => props.isEditing,
      async (value) => {
        editMode.value = value;
        await nextTick();

        if (textInput.value) {
          textInput.value.focus();
        }
      },
    );

    const inputValue = ref<string>('');
    const toggle = () => {
      if (!props.editable) return;
      editMode.value = true;
      nextTick(() => {
        const textfield = textInput.value;
        if (textfield) {
          textfield.focus();
        }

        emit('toggle-edit', true);
      });
    };

    const dismiss = () => {
      editMode.value = false;
      inputValue.value = '';
      emit('toggle-edit', false);
    };

    const changeValue = () => {
      if (!props.editable) return;

      if (inputValue.value !== '') {
        emit('value-changed', inputValue.value);
      }
    };

    watch(
      () => props.isSubmittingTitle,
      (value) => {
        if (!value) {
          editMode.value = false;
          inputValue.value = '';
        }
      },
    );

    onClickOutside(
      textInput,
      () => {
        if (props.isSubmittingTitle) return;

        if (editMode.value) {
          editMode.value = false;
          inputValue.value = '';
          emit('toggle-edit', false);
        }
      },
      {
        ignore: ['[data-headline-edit="submit"]', '[data-headline-edit="cancel"'],
      },
    );

    const attrs = useAttrs();

    return {
      textInput,
      editMode,
      inputValue,
      toggle,
      dismiss,
      changeValue,
      emit,
      HeadlineType,

      attrs,
    };
  },
  components: { FontAwesomeIcon, GpBtn },
});
</script>
